import { ROUTES } from '@/app/config/constants';
import type { MasterColumnDef, MasterCrud, MasterScreenDefinition } from '@/features/masters';
import type { TemplateRecord } from '@/features/templates';
import type { FormsApi } from '../api/formsApi';
import { purchaseRequestApi } from '../api/purchaseRequestApi';
import type { FormsChannelKey } from './formsChannelKey';
import { SUBMISSIONS_DEFINITION } from './submissionsDefinition';
import { PROCESSING_STATUS_FILTERS, PURCHASE_REQUEST_PRIORITIES } from './templateForm.types';
import type { TemplateFormSubmissionRow } from './templateForm.types';

/**
 * One forms screen: what it is called, where it lives, the RBAC feature it is scored against, and the API it
 * speaks. Purchase Requests (NB-355) is the one screen left -- the list, the Add page with its hierarchy dropdowns
 * and active template cards, and the submitted form with its workflow -- read through its channel (the Default
 * Template Forms channel was removed on 2026-10-16).
 */
export interface FormsChannel {
  key: FormsChannelKey;
  feature: string;
  /** The list's route; `${route}/new` and `${route}/:id` follow it. */
  route: string;
  /** The list, described for the masters' list table. */
  definition: MasterScreenDefinition;
  /** The Add control and page: "Add Form", "New Purchase Request". */
  addLabel: string;
  addSubtitle: string;
  searchLabel: string;
  emptyMessage: string;
  noMatchMessage: string;
  /** The submitted form's page title before it has loaded. */
  viewTitle: string;
  /** What one record is called in a sentence: "form", "purchase request". */
  noun: string;
  api: FormsApi;
  /* The paged engine's clients, built once so their identity is stable. */
  activeTemplates: MasterCrud<TemplateRecord, never>;
  submissions: MasterCrud<TemplateFormSubmissionRow, never>;
}

const refused = (message: string) => (): Promise<never> => Promise.reject(new Error(message));

function cruds(api: FormsApi) {
  const noTemplateWrites = refused('A forms screen does not write templates.');
  const noSubmissionWrites = refused('A submitted form is created through its Add page and is never edited here.');
  return {
    activeTemplates: {
      list: api.listActiveTemplates,
      get: noTemplateWrites,
      create: noTemplateWrites,
      update: noTemplateWrites,
      remove: noTemplateWrites,
    } satisfies MasterCrud<TemplateRecord, never>,
    submissions: {
      list: api.listSubmissions,
      get: api.getSubmission,
      create: noSubmissionWrites,
      update: noSubmissionWrites,
      remove: noSubmissionWrites,
    } satisfies MasterCrud<TemplateFormSubmissionRow, never>,
  };
}

/*
  design16's Purchase Requests list (2026-10-19), exactly its columns in its order -- Purchase Request Number, Purchase
  Request Title (design21, 21-10-26), Template ID, Department, Priority, Required By, Items, Processing Status -- and
  nothing else. Each is a row of the Filter dialog
  (`GET /purchase-requests` reads them in `filter`): Department, Priority and Processing Status as choices. The keys
  are the export's too.
*/
export const PR_FILTER_KEYS = {
  referenceNo: 'referenceNo',
  title: 'title',
  templateCode: 'templateCode',
  department: 'departmentNames',
  priority: 'priority',
  processingStatus: 'processingStatusLabel',
  /* The Status column (2026-10-23): Draft, or the request's own Active / Inactive -- the server's recordState. */
  recordState: 'recordState',
} as const;

/**
 * The import file's own columns, as `GET /purchase-requests/import-template` writes them (PurchaseRequestService
 * IMPORT_COLUMNS), named in the import dialog's first step as the Supplier list names its own (2026-10-23). The file
 * then carries one more column per PR Template field, by its label.
 */
export const PR_IMPORT_COLUMNS: readonly string[] = [
  /*
    Marked as the downloaded file heads them: `*` on the columns a row cannot omit, which is the marker
    `MasterTemplate` already puts on every other master's required columns and the one the record form draws
    beside a required field. The import matches a header on its letters and digits alone, so the file is
    uploaded exactly as downloaded -- nobody has to take the marker out.

    Request Key is unmarked because a row without one is simply a request of its own, and Remark because no
    PR Template pins it mandatory.
  */
  'Request Key',
  'PR Template *',
  'Business Unit *',
  'Division *',
  'Department *',
  'PR Title *',
  'Delivery Date *',
  'Description *',
  'Remark',
  'Location *',
  'Category *',
  'Product *',
  'Quantity *',
];

/** The Status column's choices: a draft, or the request's own status. */
export const PR_RECORD_STATES = ['Active', 'Inactive', 'Draft'] as const;
const PR_RECORD_STATE_OPTIONS = PR_RECORD_STATES.map((state) => ({ label: state, value: state }));

/** The Priority choices, as the form offers them and the server stores them. */
const PR_PRIORITY_OPTIONS = PURCHASE_REQUEST_PRIORITIES.map((priority) => ({ label: priority, value: priority }));

/** The Processing Status choices, worded as design16 words them. */
/* Draft is the Status column's (2026-10-23): the Processing Status offers Unprocessed, Partial Processed, Processed. */
const PR_PROCESSING_OPTIONS = PROCESSING_STATUS_FILTERS.filter((option) => option.value !== '' && option.value !== 'DRAFT').map((option) => ({
  label: option.label,
  value: option.label,
}));

const PR_COLUMNS: readonly MasterColumnDef[] = [
  {
    key: 'referenceNo',
    label: 'Purchase Request Number',
    /* design21 writes the header in capitals like every other ("PURCHASE REQUEST NUMBER"). */
    sortKey: 'referenceNo',
    filterKey: PR_FILTER_KEYS.referenceNo,
  },
  /* design21: the request's title, after its number -- sorted, filtered, searched and exported as the server's title. */
  { key: 'title', label: 'Purchase Request Title', sortKey: 'title', filterKey: PR_FILTER_KEYS.title },
  { key: 'templateCode', label: 'Template ID', sortable: false, filterKey: PR_FILTER_KEYS.templateCode },
  /* A choice of the Departments on the list; the page fills its options from the rows it reads. */
  {
    key: 'departmentNames',
    label: 'Department',
    sortable: false,
    filterKey: PR_FILTER_KEYS.department,
    filterKind: 'choice',
    filterOptions: [],
    filterPlaceholder: 'Any value',
  },
  {
    key: 'priority',
    label: 'Priority',
    sortKey: 'priority',
    filterKey: PR_FILTER_KEYS.priority,
    filterKind: 'choice',
    filterOptions: PR_PRIORITY_OPTIONS,
    filterPlaceholder: 'Any value',
  },
  { key: 'requiredBy', label: 'Required By', sortKey: 'requiredBy' },
  { key: 'itemCount', label: 'Items', sortKey: 'itemCount' },
  {
    key: 'processingStatusLabel',
    label: 'Processing Status',
    sortKey: 'processingStatus',
    filterKey: PR_FILTER_KEYS.processingStatus,
    filterKind: 'choice',
    filterOptions: PR_PROCESSING_OPTIONS,
    filterPlaceholder: 'Any value',
  },
  /* The request's Status (2026-10-23), right after Processing Status: Active, Inactive or Draft. */
  {
    key: 'recordState',
    label: 'Status',
    sortable: false,
    filterKey: PR_FILTER_KEYS.recordState,
    filterKind: 'choice',
    filterOptions: PR_RECORD_STATE_OPTIONS,
    filterPlaceholder: 'Any value',
  },
];

const PR_DEFAULT_COLUMNS = PR_COLUMNS.map((column) => column.key);

export const PURCHASE_REQUESTS_CHANNEL: FormsChannel = {
  key: 'purchase-requests',
  feature: 'Purchase Requests',
  route: ROUTES.PURCHASE_REQUESTS,
  definition: {
    ...SUBMISSIONS_DEFINITION,
    key: 'purchase-requests',
    title: 'Purchase Requests',
    recordNoun: 'Purchase Request',
    subtitle: 'Track and manage purchase requests raised across your organization.',
    icon: 'pi-clipboard',
    codePrefix: 'PR',
    codeLabel: 'Purchase Request Number',
    nameLabel: 'PR Template',
    columns: PR_COLUMNS,
    defaultColumns: PR_DEFAULT_COLUMNS,
    /* Every row action a request has; the list keeps each to its RBAC action and the row's state. */
    actions: ['view', 'edit', 'duplicate', 'delete'],
    /*
      Fitted to the window as Incoterm's list is (2026-10-23): the rows scroll inside the table under its header, the
      pager held below; its page links run 1 2 3 … last (design16).
    */
    pageLinks: 'ellipsis',
    actionsHeader: 'ACTION',
    /* design16: its row menu items have no hover fill. */
    rowMenuHover: 'none',
  },
  addLabel: 'New Purchase Request',
  addSubtitle: 'Fill in the request for the selected hierarchy and send it for approval.',
  searchLabel: 'Search Purchase Request Number, Title, Template ID or Department',
  /* design16's empty state, its title; the page draws the hint under it. */
  emptyMessage: 'No purchase requests found',
  noMatchMessage: 'No purchase requests found',
  viewTitle: 'Purchase Request',
  noun: 'purchase request',
  api: purchaseRequestApi,
  ...cruds(purchaseRequestApi),
};
